import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { LogOut, Settings, Menu, X } from 'lucide-react';
import UploadDocument from '@/components/UploadDocument';
import BulkUpload from '@/components/BulkUpload';
import ViewDocuments from '@/components/ViewDocuments';
import AppHeader from '@/components/AppHeader';
import { useNavigate } from 'react-router-dom';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upload');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between">
            <AppHeader title="Vistara Verify" subtitle="Internal Dashboard" />
            
            <div className="flex items-center gap-3 lg:gap-4">
              <div className="text-right hidden lg:block">
                <p className="text-sm font-medium text-slate-900" data-testid="user-name">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/settings')}
                className="flex items-center gap-2"
                size="sm"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Settings</span>
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="flex items-center gap-2"
                data-testid="logout-button"
                size="sm"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>

          {/* Mobile Header */}
          <div className="flex md:hidden items-center justify-between">
            <AppHeader title="Vistara Verify" subtitle="" />
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-3 pt-3 border-t border-slate-200 space-y-3">
              <div className="px-1">
                <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    navigate('/settings');
                    setMobileMenuOpen(false);
                  }}
                  className="flex-1 h-10"
                  size="sm"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="flex-1 h-10"
                  size="sm"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          {/* Tab Navigation - Scrollable on mobile */}
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-auto min-w-full sm:w-full sm:max-w-2xl sm:grid sm:grid-cols-3 h-11 sm:h-12 p-1 bg-slate-100 rounded-lg">
              <TabsTrigger 
                value="upload" 
                className="flex-1 min-w-[120px] sm:min-w-0 text-sm sm:text-base whitespace-nowrap px-3 sm:px-4" 
                data-testid="upload-tab"
              >
                Upload
              </TabsTrigger>
              <TabsTrigger 
                value="bulk" 
                className="flex-1 min-w-[120px] sm:min-w-0 text-sm sm:text-base whitespace-nowrap px-3 sm:px-4" 
                data-testid="bulk-tab"
              >
                Bulk Upload
              </TabsTrigger>
              <TabsTrigger 
                value="view" 
                className="flex-1 min-w-[120px] sm:min-w-0 text-sm sm:text-base whitespace-nowrap px-3 sm:px-4" 
                data-testid="view-tab"
              >
                View All
              </TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="upload" className="mt-4 sm:mt-6">
            <UploadDocument />
          </TabsContent>
          
          <TabsContent value="bulk" className="mt-4 sm:mt-6">
            <BulkUpload />
          </TabsContent>
          
          <TabsContent value="view" className="mt-4 sm:mt-6">
            <ViewDocuments />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
